import { Controller } from "@hotwired/stimulus"
import { viewerTimeZone } from "../lib/viewer_time_zone"

// The duration menu behind every snooze button. The controller sits on the single panel
// (see layouts/application) rather than on each trigger, and listens on the document — the
// same singleton the time popover uses, and for the same reason: fifty rows would put fifty
// hidden panels in the DOM for the one that is ever open.
//
// It cannot reuse dropdown_controller: that one assumes the menu is a descendant of its own
// element (closeOnOutside tests this.element.contains) and that a hidden field and a form
// are nearby. This panel is parented to the body, and the form action is rewritten from the
// invoking button's conversation on each open.
//
// Custom times post a timezone-less datetime-local value. The server parses it in the reader's
// zone (lib/viewer_time_zone) — the same zone the presets use. JS must not turn that string into
// a Date and back (that would bake in the browser's offset) and must not compare it as a local
// Date either.
const VIEWPORT_MARGIN = 8
const TRIGGER_GAP = 6
const TRIGGER_SELECTOR = "[data-snooze-popover]"

export default class extends Controller {
  static targets = ["form", "presets", "custom", "wakeAt", "error"]

  connect() {
    this.openFromEvent = this.openFromEvent.bind(this)
    this.closeOnEscape = this.closeOnEscape.bind(this)
    this.closeOnScroll = this.closeOnScroll.bind(this)
    this.onSubmit = this.onSubmit.bind(this)

    document.addEventListener("click", this.openFromEvent)
    document.addEventListener("keydown", this.closeOnEscape)
    // Capturing, because the list that would shear this panel is .app__list — a scroll
    // event does not bubble out of it to the document. Without this a fixed menu stays
    // where it was painted while the button it belongs to slides away.
    document.addEventListener("scroll", this.closeOnScroll, true)
    this.formTarget.addEventListener("submit", this.onSubmit)
  }

  disconnect() {
    this.close()
    document.removeEventListener("click", this.openFromEvent)
    document.removeEventListener("keydown", this.closeOnEscape)
    document.removeEventListener("scroll", this.closeOnScroll, true)
    this.formTarget.removeEventListener("submit", this.onSubmit)
  }

  openFromEvent(event) {
    const trigger = event.target.closest?.(TRIGGER_SELECTOR)
    if (trigger) {
      this.toggle(trigger)
      return
    }

    if (!this.element.hidden && !this.element.contains(event.target)) this.close()
  }

  toggle(trigger) {
    if (this.trigger === trigger && !this.element.hidden) {
      this.close()
      return
    }

    this.open(trigger)
  }

  open(trigger) {
    this.trigger = trigger
    this.resetToPresets()
    this.formTarget.action = trigger.dataset.snoozeUrl
    // One panel serves every row and the bulk bar, so which of the two this submission is cannot be
    // markup on the panel — it is a fact about the button that opened it. Rewritten alongside the
    // action for that reason: selection_controller stamps the ticked ids onto a form carrying this
    // marker, and a row's own snooze must not carry a selection it knows nothing about.
    this.formTarget.toggleAttribute("data-bulk-action", "snoozeBulk" in trigger.dataset)
    this.element.hidden = false
    this.position(trigger)
    this.markExpanded(true)
    // Focusing the first option can make the browser scroll a container into view,
    // and the capturing scroll listener would otherwise close the menu on the same
    // click that opened it.
    this.ignoreScroll = true
    this.firstOption?.focus()
    requestAnimationFrame(() => { this.ignoreScroll = false })
  }

  close() {
    if (this.element.hidden) return

    this.resetToPresets()
    this.dismiss()
  }

  dismiss() {
    this.element.hidden = true
    this.markExpanded(false)
    this.trigger = null
  }

  showCustom() {
    this.presetsTarget.hidden = true
    this.customTarget.hidden = false
    this.wakeAtTarget.disabled = false
    this.fillDefaultWakeAt()
    this.element.setAttribute("role", "dialog")
    this.element.setAttribute("aria-label", "Pick date and time")
    this.reposition()
    this.wakeAtTarget.focus()
  }

  showPresets() {
    this.resetToPresets()
    this.reposition()
    this.firstOption?.focus()
  }

  closeOnEscape(event) {
    if (event.key !== "Escape" || this.element.hidden) return

    const trigger = this.trigger
    this.close()
    trigger?.focus()
  }

  closeOnScroll() {
    if (this.ignoreScroll) return

    this.close()
  }

  onSubmit(event) {
    if (!this.customTarget.hidden && !this.customWakeIsInTheFuture()) {
      event.preventDefault()
      this.showError("Choose a time in the future.")
      return
    }

    // Hide without resetToPresets: disabling the datetime field here would drop
    // snoozed_until from the submission the browser is about to serialize.
    this.dismiss()
  }

  // Below the trigger by default, flipped above when there is no room. Aligned to the
  // trigger's right edge — snooze sits on the row's right, and a left-aligned menu would
  // hang off the list into the thread pane.
  position(trigger) {
    const anchor = trigger.getBoundingClientRect()
    const panel = this.element.getBoundingClientRect()

    const below = anchor.bottom + TRIGGER_GAP
    const top = below + panel.height > window.innerHeight - VIEWPORT_MARGIN
      ? anchor.top - panel.height - TRIGGER_GAP
      : below

    const rightAligned = anchor.right - panel.width
    const rightmost = window.innerWidth - panel.width - VIEWPORT_MARGIN
    const left = Math.max(VIEWPORT_MARGIN, Math.min(rightAligned, rightmost))

    this.element.style.top = `${Math.max(VIEWPORT_MARGIN, top)}px`
    this.element.style.left = `${left}px`
  }

  markExpanded(open) {
    document.querySelectorAll(`${TRIGGER_SELECTOR}[aria-expanded]`).forEach((button) => {
      button.setAttribute("aria-expanded", button === this.trigger && open ? "true" : "false")
    })
  }

  resetToPresets() {
    this.customTarget.hidden = true
    this.presetsTarget.hidden = false
    this.wakeAtTarget.disabled = true
    this.wakeAtTarget.value = ""
    this.clearError()
    this.element.setAttribute("role", "menu")
    this.element.setAttribute("aria-label", "Snooze until")
  }

  fillDefaultWakeAt() {
    const soon = new Date(Date.now() + 60 * 60 * 1000)
    this.wakeAtTarget.min = this.stampInAppZone(new Date())
    this.wakeAtTarget.value = this.stampInAppZone(soon)
  }

  // String compare of YYYY-MM-DDTHH:MM, which is what datetime-local stores. Building a
  // Date from that string would interpret it as the browser's local time.
  customWakeIsInTheFuture() {
    const chosen = this.wakeAtTarget.value
    if (!chosen) return false

    return chosen > this.stampInAppZone(new Date())
  }

  stampInAppZone(instant) {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: viewerTimeZone(),
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23"
    }).formatToParts(instant)

    const value = (type) => parts.find((part) => part.type === type)?.value

    return `${value("year")}-${value("month")}-${value("day")}T${value("hour")}:${value("minute")}`
  }

  showError(message) {
    this.errorTarget.hidden = false
    this.errorTarget.textContent = message
  }

  clearError() {
    this.errorTarget.hidden = true
    this.errorTarget.textContent = ""
  }

  reposition() {
    if (this.trigger) this.position(this.trigger)
  }

  get firstOption() {
    return this.element.querySelector("[role='menuitem']")
  }
}
